import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { UrlFormatter } from 'shared';
import { ParseService } from './parse.service';
import { DownloadService } from './download.service';

/**
 * Native-Angular port of the AngularJS `PhotoContest` service
 * (account-v1/src/core/api/photoAdvocacy.js). Verbatim: relative `/api/...` URLs
 * via the native `UrlFormatter.format`, Promise-returning methods (`firstValueFrom`
 * yields the response body = the v1 `$http…then(r => r.data)` value), the same
 * `setupContest`/`setupPhoto`/`setupSubmission` coercions (via the native
 * `ParseService`), the `languageHelper.sortByDefaultLanguage` reproduced inline
 * (en-default stable sort), `angular.copy` → `structuredClone`, `angular.extend`
 * → spread, `angular.forEach` → `ngForEach`, and the blob `downloadShareImage`
 * via the native `DownloadService.getFileNameFromHeaders` (framework-agnostic).
 *
 * WS5 #10 Stage A inc 2b: replaces the `$injector.get('PhotoContest')`
 * bridge. `providedIn:'root'` so consumers `inject()` it directly. The reused
 * redux-thunks / native components call the same method names with the same shapes.
 */
@Injectable({ providedIn: 'root' })
export class PhotoContestApiService {
  private readonly http = inject(HttpClient);
  private readonly parse = inject(ParseService);
  private readonly download = inject(DownloadService);

  load(model: any): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(UrlFormatter.format('/api/:propertyUuid/properties/layers/photocontest/contests/:contestId/data', model)),
    ).then((data) => {
      sortByDefaultLanguage(data.languages);
      return data;
    });
  }

  past(propertyId: string): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(
        UrlFormatter.format('/api/:propertyUUID/properties/layers/:propertyUUID/photocontest/contests/load/past', { propertyUUID: propertyId }),
      ),
    );
  }

  future(propertyId: string): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(
        UrlFormatter.format('/api/:propertyUUID/properties/layers/:propertyUUID/photocontest/contests/load/future', { propertyUUID: propertyId }),
      ),
    );
  }

  update(propertyUuid: string, contestId: string, model: any): Promise<any> {
    return firstValueFrom(
      this.http.put<any>(
        UrlFormatter.format('/api/:propertyUuid/properties/layers/photocontest/contests/:contestId/data', { contestId, propertyUuid }),
        model,
      ),
    );
  }

  submissions(propertyUuid: string, contestId: string): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(
        UrlFormatter.format('/api/:propertyUuid/properties/layers/photocontest/contests/:contestId/submissions', { contestId, propertyUuid }),
      ),
    ).then((data) => {
      if (!data) return;
      const contest = data;
      this.setupContest(contest);
      if (contest.submissions) ngForEach(contest.submissions.item, (s: any) => this.setupSubmission(s));
      return contest;
    });
  }

  submissionsWithPaging(propertyUuid: string, contestId: string, paging: any): Promise<any> {
    const params = { contestId, propertyUuid, ...paging };
    return firstValueFrom(
      this.http.get<any>(
        UrlFormatter.format('/api/:propertyUuid/properties/layers/photocontest/contests/:contestId/submissions-paging', params, true),
      ),
    ).then((data) => {
      if (!data) return;
      const contest = data;
      this.setupContest(contest);
      if (contest.submissions) ngForEach(contest.submissions.item, (s: any) => this.setupSubmission(s));
      return contest;
    });
  }

  photo(propertyUuid: string, photoId: string): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(
        UrlFormatter.format('/api/:propertyUuid/properties/layers/photocontest/contests/photos/:photoId', { photoId, propertyUuid }),
      ),
    ).then((data) => {
      this.setupPhoto(data);
      return data;
    });
  }

  finalists(propertyUuid: string, contestId: string): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(
        UrlFormatter.format('/api/:propertyUuid/properties/layers/photocontest/contests/:contestId/finalists', { contestId, propertyUuid }),
      ),
    );
  }

  finalistsWithPaging(propertyUuid: string, contestId: string, paging: any): Promise<any> {
    const params = { contestId, propertyUuid, ...paging };
    return firstValueFrom(
      this.http.get<any>(
        UrlFormatter.format('/api/:propertyUuid/properties/layers/photocontest/contests/:contestId/finalists-paging', params, true),
      ),
    );
  }

  create(propertyId: string, model: any): Promise<string> {
    return firstValueFrom(
      this.http.post(
        UrlFormatter.format('/api/:propertyId/properties/:propertyId/layers/photocontest/contests/create', { propertyId }),
        model,
        { responseType: 'text' },
      ),
    ).then((data) => data.replace(/"/gi, ''));
  }

  clone(propertyId: string, model: any): Promise<string> {
    return firstValueFrom(
      this.http.post(
        UrlFormatter.format('/api/:propertyId/properties/:propertyId/layers/photocontest/contests/clone', { propertyId }),
        model,
        { responseType: 'text' },
      ),
    ).then((data) => data.replace(/"/gi, ''));
  }

  activate(propertyUuid: string, contestId: string, isActive: any): Promise<any> {
    return firstValueFrom(
      this.http.put<any>(
        UrlFormatter.format('/api/:propertyUuid/properties/layers/photocontest/contests/:contestId/activate/:isActive', { contestId, isActive, propertyUuid }),
        null,
      ),
    );
  }

  updateWinners(propertyUuid: string, contestId: string, winners: any): Promise<any> {
    return firstValueFrom(
      this.http.put<any>(
        UrlFormatter.format('/api/:propertyUuid/properties/layers/photocontest/contests/:contestId/winners/update', { contestId, propertyUuid }),
        winners,
      ),
    );
  }

  closeContest(propertyUuid: string, contestId: string): Promise<any> {
    return firstValueFrom(
      this.http.put<any>(
        UrlFormatter.format('/api/:propertyUuid/properties/layers/photocontest/contests/:contestId/close', { contestId, propertyUuid }),
        null,
      ),
    );
  }

  cleanContestCheaters(propertyUuid: string, contestId: string, cutoffDate: any): Promise<any> {
    return firstValueFrom(
      this.http.put<any>(
        UrlFormatter.format('/api/:propertyUuid/properties/layers/photocontest/contests/:contestId/cleanCheaters', { contestId, cutoffDate, propertyUuid }, true),
        null,
      ),
    );
  }

  ratePhoto(propertyUuid: string, photoUUID: string, rating: any): Promise<any> {
    return firstValueFrom(
      this.http.put<any>(
        UrlFormatter.format('/api/:propertyUuid/properties/layers/photo-advocacy/contests/photos/:photoUUID/internal-rating/set', { photoUUID, propertyUuid }),
        rating,
      ),
    );
  }

  rateCaption(propertyUuid: string, photoUUID: string, rating: any): Promise<any> {
    return firstValueFrom(
      this.http.put<any>(
        UrlFormatter.format('/api/:propertyUuid/properties/layers/photo-advocacy/contests/photos/:photoUUID/caption-rating/set', { photoUUID, propertyUuid }),
        rating,
      ),
    );
  }

  rateQuote(propertyUUID: string, guestUUID: string, rating: any): Promise<any> {
    return firstValueFrom(
      this.http.put<any>(
        UrlFormatter.format('/api/:propertyUUID/properties/:propertyUUID/guests/:guestUUID/quote/rating', { propertyUUID, guestUUID }),
        rating,
      ),
    );
  }

  setPhotoInfo(propertyUuid: string, photoId: string, model: any): Promise<any> {
    return firstValueFrom(
      this.http.put<any>(
        UrlFormatter.format('/api/:propertyUuid/properties/layers/photo-advocacy/contests/photos/:photoId/contest-photo-info/set', { photoId, propertyUuid }),
        model,
      ),
    );
  }

  reprocessPhoto(propertyUuid: string, photoUuid: string): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(
        UrlFormatter.format('/api/:propertyUuid/properties/layers/photo-advocacy/contests/photos/:photoUuid/reprocess', { photoUuid, propertyUuid }),
        null,
      ),
    );
  }

  details(propertyUUID: string, guestUUID: string): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(
        UrlFormatter.format('/api/:propertyUUID/properties/:propertyUUID/guests/:guestUUID/curation', { propertyUUID, guestUUID }),
      ),
    )
      .then((data) => structuredClone(data))
      .then((curation) => {
        if (curation.guest.poststay.photo) {
          this.setupSubmission(curation.guest.poststay.photo);
        }
        curation.isFinalistEmailActive = this.parse.bool(curation.isFinalistEmailActive);
        curation.socialNetworksSharing = this.parse.arrayOrDefault(curation.socialNetworksSharing);

        // Post-processing to make the ai tags more usable
        if (curation.guest.poststay.photo) {
          const photo = curation.guest.poststay.photo;
          photo.ai = photo.ai || {};

          // Loop through tag categories
          for (let i = 0; i < photo.ai.aiTagCategories.length; i++) {
            const category = photo.ai.aiTagCategories[i];

            // Save any tags found in a new property named after the category attribute
            if (category.aiTags) {
              curation.guest.poststay.photo.ai[category['@category']] = this.parse.arrayOrDefault(category.aiTags);
            }
          }
          delete curation.guest.poststay.photo.ai.aiTagCategories;

          // Also parse the list of faces
          photo.ai.faces = this.parse.arrayOrDefault(photo.ai.faces);
        }
        return curation;
      });
  }

  advanceToFinal(propertyUuid: string, photoId: string): Promise<any> {
    return firstValueFrom(
      this.http.put<any>(
        UrlFormatter.format('/api/:propertyUuid/properties/layers/photo-advocacy/contests/photos/:photoId/finalist/set', { photoId, propertyUuid }),
        true,
      ),
    );
  }

  pass(propertyUuid: string, photoId: string): Promise<any> {
    return firstValueFrom(
      this.http.put<any>(
        UrlFormatter.format('/api/:propertyUuid/properties/layers/photo-advocacy/contests/photos/:photoId/finalist/set', { photoId, propertyUuid }),
        false,
      ),
    );
  }

  updateBackgroundImage(propertyUuid: string, contestId: string, image: any): Promise<any> {
    return firstValueFrom(
      this.http.put<any>(
        UrlFormatter.format('/api/:propertyUuid/properties/layers/photocontest/contests/:contestId/update-background-image', { contestId, propertyUuid }),
        { backgroundImage: image },
      ),
    );
  }

  generatePreArrivalEmailImage(propertyUuid: string, contestId: string, culture: string): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(
        UrlFormatter.format('/api/:propertyUuid/properties/layers/photo-advocacy/contests/:contestId/generate-pre-arrival-image', { contestId, languageCode: culture, propertyUuid }, true),
        null,
      ),
    );
  }

  generatePoststayEmailImage(propertyUuid: string, contestId: string, culture: string): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(
        UrlFormatter.format('/api/:propertyUuid/properties/layers/photo-advocacy/contests/:contestId/generate-poststay-image', { contestId, languageCode: culture, propertyUuid }, true),
        null,
      ),
    );
  }

  createCurationShareImage(propertyUuid: string, model: any): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(UrlFormatter.format('/api/' + propertyUuid + '/social-network-sharing/create-curation-image', model), model),
    );
  }

  createInvitationShareImage(propertyUuid: string, model: any): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(UrlFormatter.format('/api/' + propertyUuid + '/social-network-sharing/create-contest-invitation-image', model), model),
    );
  }

  createInvitationShareImageFromTemplate(propertyUuid: string, model: any): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(UrlFormatter.format('/api/' + propertyUuid + '/social-network-sharing/create-contest-invitation-image-from-template', model), model),
    );
  }

  getInvitationShareImageTemplate(propertyUuid: string, model: any): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(UrlFormatter.format('/api/' + propertyUuid + '/social-network-sharing/contest-invitation-image-template', model), model),
    );
  }

  downloadShareImage(propertyUuid: string, imageUrl: string): Promise<any> {
    return firstValueFrom(
      this.http.get(UrlFormatter.format('/api/:propertyUuid/social-network-sharing/download-image', { imageUrl, propertyUuid }, true), {
        responseType: 'blob',
        observe: 'response',
      }),
    ).then((response) => ({
      content: response.body,
      fileName: this.download.getFileNameFromHeaders(response),
    }));
  }

  private setupContest(contest: any): any {
    const intProperties = ['totalFinalists', 'totalFinalistsScore', 'totalNewPhotoSubmissions', 'totalInviteePhotoSubmissions', 'totalPhotoSubmissions', 'totalRunnerUps', 'totalWinners'];
    intProperties.forEach((property) => {
      contest[property] = this.parse.intOrDefault(contest[property]);
    });
    sortByDefaultLanguage(contest.languages);
    return contest;
  }

  private setupPhoto(photo: any): any {
    const intProperties = ['quoteInternalRating', 'quoteRating'];
    intProperties.forEach((property) => {
      photo[property] = this.parse.intOrDefault(photo[property]);
    });
    return photo;
  }

  private setupSubmission(submission: any): any {
    const boolProperties = ['isNewSubmission', 'isUpdated', 'isFinalist', 'isPassed'];
    boolProperties.forEach((property) => {
      submission[property] = this.parse.bool(submission[property]);
    });
    return submission;
  }
}

/** App default language — the v1 `defaultLanguage` provider's default ('en'); the
 * account app is single-locale en (LOCALE_ID 'en', translate 'en'). */
const DEFAULT_LANGUAGE = 'en';

/**
 * Verbatim port of `languageHelper.sortByDefaultLanguage` (account-v1
 * core/language/language.js): sort `languages.item` so the default-language entry
 * is first (key `'0'`), the rest by `name`. Reproduces lodash `_.sortBy` — STABLE
 * (index tiebreaker) and, for a single-object `.item`, sorts its values into an
 * array (the same shape change `_.sortBy` produces). Mutates `languages.item`.
 */
function sortByDefaultLanguage(languages: any): void {
  if (languages && languages.item) {
    const item = languages.item;
    const arr: any[] = Array.isArray(item) ? item : Object.keys(item).map((k) => item[k]);
    languages.item = arr
      .map((lang, i) => ({ lang, i, key: lang.code === DEFAULT_LANGUAGE ? '0' : lang.name }))
      .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : a.i - b.i))
      .map((d) => d.lang);
  }
}

/**
 * Replicates `angular.forEach` for `.item` collections (array → elements, single
 * object → own-property values, null → no-op) so the v1 behaviour is preserved
 * regardless of whether the API returns one or many.
 */
function ngForEach(collection: any, fn: (value: any) => void): void {
  if (collection == null) return;
  if (Array.isArray(collection)) {
    collection.forEach((v) => fn(v));
  } else if (typeof collection === 'object') {
    Object.keys(collection).forEach((k) => fn(collection[k]));
  }
}
